import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const SearchingVideos: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/help')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('help.backToHelp', 'Back to Help Center')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          Getting Started
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          How to Search for Videos
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          IndeeClips search helps you find exactly what you're looking for. Learn how to use keywords, filters, and advanced search techniques to discover content faster.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Basic Search
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Click the search icon (magnifying glass) in the top navigation bar</li>
          <li>Type your keyword or phrase</li>
          <li>Press Enter or click the search button</li>
          <li>Results appear with videos, channels, and clips matching your query</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Search Tips for Better Results
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Be specific:</strong> "Chicken biryani recipe" {'>'} "biryani"</li>
          <li><strong>Use keywords:</strong> Include relevant terms like "tutorial", "review", "how to"</li>
          <li><strong>Language matters:</strong> Search in Hindi if looking for Hindi content ("मोबाइल रिव्यू")</li>
          <li><strong>Channel names:</strong> Search "@channelname" to find specific creators</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Search Filters
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          After searching, refine results using filters:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Upload Date:</strong> Last hour, Today, This week, This month, This year</li>
          <li><strong>Duration:</strong> Under 4 minutes, 4-20 minutes, Over 20 minutes</li>
          <li><strong>Sort By:</strong> Relevance, Upload date, View count, Rating</li>
          <li><strong>Type:</strong> Videos, Clips, Channels</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Searching Within a Channel
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Visit the channel page</li>
          <li>Click the search icon within the channel</li>
          <li>Type your keyword</li>
          <li>Results show only videos from that specific channel</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Useful for finding old videos from prolific creators with hundreds of uploads.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Voice Search (Mobile)
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Tap the search bar</li>
          <li>Tap the microphone icon</li>
          <li>Speak your query clearly</li>
          <li>Search executes automatically when you finish speaking</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Search History
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          IndeeClips saves your recent searches for quick access.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>View history:</strong> Click search bar to see recent queries</li>
          <li><strong>Delete individual searches:</strong> Click X next to any search term</li>
          <li><strong>Clear all history:</strong> Settings → Privacy → Clear Search History</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Advanced Search Tricks
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Use operators for precise searches:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Exact phrase:</strong> Use quotes → "best smartphone 2026"</li>
          <li><strong>Exclude words:</strong> Use minus sign → smartphone -iPhone</li>
          <li><strong>Channel filter:</strong> Use @ → @TechGuru smartphone review</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Common Search Issues
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
          "No results found"
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Check spelling</li>
          <li>Try broader keywords ("cooking" instead of "Hyderabadi biryani recipe")</li>
          <li>Search in a different language</li>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, mt: 3, fontWeight: 600 }}>
          "Results not relevant"
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Add more specific keywords</li>
          <li>Use filters (date, duration, sort by relevance)</li>
          <li>Try exact phrase search with quotes</li>
        </Box>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Can't Find What You're Looking For?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Contact support at{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default SearchingVideos;
